import React, { useState, useEffect } from "react"
import Config from "react-global-configuration"
import { Box, Divider, TextField, Grid, Button, makeStyles, Hidden } from "@material-ui/core"
import { AccountBox } from "@material-ui/icons"
import { useAuth } from "../../services"
import {
  FieldSectionHeading,
  LinkButton,
  UploaderProfileImage,
  useSnackbar,
  LoadingSpinner,
  UserPanel,
  PinInput,
} from "../../components"
import { useMutationUpdateSelf, useQueryAdminableUser } from "../../data"
// import { useFormUtils } from '../../utils'

const useStyles = makeStyles((theme) => ({
  textField: {
    marginBottom: theme.spacing(1),
  },
  avatar: {
    width: 80,
    height: 80,
    fontSize: 24,
  },
  image: {
    backgroundColor: theme.palette.shaded.main,
    borderRadius: 4,
  },
  xsUserPanel: {
    width: "100% !important",
  },
}))

const Profile = () => {
  const classes = useStyles()
  // const { isValid, isError, getErrorMessage, debounceTouched } = useFormUtils()
  const { principal, updatePrincipal } = useAuth()
  const { pinLength } = Config.get("auth")
  const { data } = useQueryAdminableUser({ variables: { id: principal.userID, organisation: principal.organisation } })
  const [updateSelf] = useMutationUpdateSelf()
  const snackbar = useSnackbar()

  const { external } = principal

  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState(false)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [avatar, setAvatar] = useState(null)
  const [pin, setPin] = useState("")
  const [clearPin, setClearPin] = useState(false)

  useEffect(() => {
    if (data) {
      const { adminableUser: user } = data
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setEmail(user.email)
      setAvatar(user.avatar)
    }
  }, [data])

  useEffect(() => {
    if (clearPin) setClearPin(false)
  }, [clearPin])

  const handleSave = async () => {
    try {
      setLoading(true)
      const variables = { firstName, lastName, avatar: avatar && avatar.id }
      if (pin) variables.pin = pin
      await updateSelf({ variables })
      updatePrincipal({ firstName, lastName, avatar: avatar && avatar.key })
      setLoading(false)
      setTouched(false)
      setPin("")
      setClearPin(true)
      snackbar.showMessage({ message: "Profile updated", icon: <AccountBox /> })
    } catch (error) {
      snackbar.showMessage({ message: error.message })
    }
  }

  const handleCancel = () => {
    const { adminableUser: user } = data
    setFirstName(user.firstName)
    setLastName(user.lastName)
    setEmail(user.email)
    setAvatar(user.avatar)
    setPin("")
    setTouched(false)
  }

  const handleChange = (set, oldValue, newValue) => {
    if (newValue !== oldValue) {
      if (!touched) setTouched(true)
      set(newValue)
    }
  }

  const handleAvatarChange = (file) => {
    if (file) {
      setAvatar(file.upload)
    } else {
      setAvatar(null)
    }
    setTouched(true)
  }

  return (
    <>
      <Box mb={5}>
        {!data && (
          <Box display="flex" justifyContent="center">
            <LoadingSpinner size={60} />
          </Box>
        )}
        {data && (
          <>
            <Hidden lgUp xsDown>
              <UserPanel user={data.adminableUser} layout="landscape" mb={2} />
            </Hidden>
            <Hidden smUp>
              <UserPanel user={data.adminableUser} layout="portrait" mb={2} className={classes.xsUserPanel} />
            </Hidden>

            <FieldSectionHeading size="large">Personal information</FieldSectionHeading>
            <Box mb={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="First name"
                    value={firstName}
                    onChange={(event) => handleChange(setFirstName, firstName, event.target.value)}
                    className={classes.textField}
                    required
                    disabled={loading || external}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Last name"
                    value={lastName}
                    onChange={(event) => handleChange(setLastName, lastName, event.target.value)}
                    className={classes.textField}
                    required
                    disabled={loading || external}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Email address"
                    value={email}
                    onChange={(event) => handleChange(setEmail, email, event.target.value)}
                    className={classes.textField}
                    required
                    disabled={loading || true}
                  />
                  {!external && (
                    <small>Please contact support if you would like your login / email address changed</small>
                  )}
                  {external && <small>Your user is managed externally, editing is disabled for some fields</small>}
                </Grid>
              </Grid>
            </Box>

            <FieldSectionHeading size="large">Profile image</FieldSectionHeading>
            <UploaderProfileImage
              avatarProps={{ firstName, lastName, avatar }}
              subject="photo"
              onChange={handleAvatarChange}
            />

            <FieldSectionHeading size="large">Change tablet PIN</FieldSectionHeading>
            <Box mb={3}>
              <PinInput
                length={pinLength}
                onChange={(value) => handleChange(setPin, pin, value)}
                className={classes.pin}
                allowShow
                password
                clear={clearPin}
                display={null}
                justifyContent={null}
              />
            </Box>
          </>
        )}
      </Box>

      <Divider />

      <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center" mt={2}>
        <Box mr={3}>
          <LinkButton
            onClick={handleCancel}
            className={`${classes.button} ${classes.buttonText}`}
            disabled={!touched || loading}
          >
            Cancel
          </LinkButton>
        </Box>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!touched || loading}>
          <Box px={2}>Save</Box>
        </Button>
      </Box>
    </>
  )
}

export default Profile
